/**
 * Approve, send back, or reject — wherever a recording is being judged.
 *
 * Lives in one place because there are now two: the review queue, and a
 * contributor's own page, where somebody looking over a run of work wants to
 * act on what they find without going and hunting for it in the queue. Two
 * copies of this would drift, and the half that drifts is the half where a
 * rejection goes out without a reason.
 *
 * The reason is required for anything that is not an approval, which is what
 * the API enforces — the contributor is told why, and the wording has to map
 * to something in the terms they agreed to rather than a reviewer's
 * shorthand.
 */
import { useState } from "react";
import type { ReviewDecision as Decision } from "../types/api";

/** The rejection reasons, matching models/review.py. Not exported: the
 *  only thing that needs them is the control below, and exporting a
 *  constant from a component file costs fast refresh. */
const REASONS: Array<{ id: string; label: string }> = [
  { id: "inaudible", label: "Can't hear it clearly" },
  { id: "too_short", label: "Too short" },
  { id: "too_long", label: "Too long" },
  { id: "off_topic", label: "Off topic" },
  { id: "script_read", label: "Read like a script" },
  { id: "ai_generated", label: "Sounds AI-generated" },
  { id: "over_produced", label: "Over-produced" },
  { id: "duplicate", label: "Duplicate" },
  { id: "silent", label: "Silent" },
  { id: "guideline_breach", label: "Breaches guidelines" },
  { id: "other", label: "Other" },
];

interface Props {
  recordingId: string;
  busy: boolean;
  onDecide: (
    recordingId: string,
    decision: Decision,
    reason?: string,
    note?: string,
  ) => void | Promise<void>;
  /**
   * False on a take that is already approved. The ledger credits once per
   * recording, so approving again does nothing — and a button that does
   * nothing while looking like it worked is worse than no button.
   */
  canApprove?: boolean;
  /** Shown above the buttons when the verdict is being changed, not set. */
  warning?: string;
}

export function ReviewDecision({
  recordingId,
  busy,
  onDecide,
  canApprove = true,
  warning,
}: Props) {
  const [mode, setMode] = useState<"idle" | "rerecord" | "rejected">("idle");
  const [reason, setReason] = useState(REASONS[0].id);
  const [note, setNote] = useState("");

  if (mode === "idle") {
    return (
      <>
        {warning && (
          <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>
            {warning}
          </div>
        )}
        <div className="btn-row mt-16">
          {canApprove && (
            <button
              type="button"
              className="btn btn-primary"
              style={{ width: "auto" }}
              disabled={busy}
              onClick={() => void onDecide(recordingId, "approved")}
            >
              {busy ? "Working…" : "Approve & pay"}
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={() => setMode("rerecord")}
          >
            Ask for another take
          </button>
          <button
            type="button"
            className="btn btn-danger-ghost"
            disabled={busy}
            onClick={() => setMode("rejected")}
          >
            Reject
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="review-reason mt-16">
      <div className="field">
        <label htmlFor={`reason-${recordingId}`}>Reason (required)</label>
        <select
          id={`reason-${recordingId}`}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        >
          {REASONS.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.label}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor={`note-${recordingId}`}>Note to the contributor</label>
        <input
          id={`note-${recordingId}`}
          value={note}
          placeholder="They'll see this in their log"
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
      <div className="btn-row">
        <button
          type="button"
          className="btn btn-primary"
          style={{ width: "auto" }}
          disabled={busy}
          onClick={() =>
            void onDecide(recordingId, mode as Decision, reason, note || undefined)
          }
        >
          {busy ? "Working…" : mode === "rerecord" ? "Send back" : "Confirm rejection"}
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => setMode("idle")}>
          Cancel
        </button>
      </div>
    </div>
  );
}
